/**
 * Google Identity Services sign-in.
 *
 * Ported from google_auth.js. Two changes: the client id is overridable from the
 * environment (falling back to the production one, see below), and the GSI
 * script is loaded on demand rather than being a blocking tag in every page
 * load. The two-step flow — verify, then complete with a chosen role for a new
 * user — is unchanged, since the backend implements exactly that.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { authApi } from '../api/endpoints';

const GSI_SRC = 'https://accounts.google.com/gsi/client';
const GSI_SCRIPT_ID = 'capsule-google-identity';

/**
 * The production client id, as a fallback when the environment does not set one.
 *
 * OAuth client ids are public by design — they are embedded in every page that
 * uses Google Identity Services, and the legacy `google_auth.js` hard-coded this
 * same value and served it to every visitor. It is not a secret.
 *
 * Defaulting it matters because the alternative is a silent regression: without
 * a value the sign-in button simply does not render, and a deployment that
 * forgot to create .env loses Google sign-in with no error anywhere. The backend
 * still verifies every credential against GOOGLE_CLIENT_ID server-side, so a
 * wrong id here fails loudly at verification rather than authenticating anyone.
 */
const DEFAULT_GOOGLE_CLIENT_ID =
  '934884489582-oout0so0t0e9rmqlgtv8da9h060u38rf.apps.googleusercontent.com';

/** Roles the backend's google-complete route accepts. */
export const GOOGLE_ROLES = [
  { value: 'patient', label: 'مستخدم / مريض' },
  { value: 'doctor', label: 'طبيب' },
  { value: 'team_member', label: 'عضو فريق التسويق' },
];

/** Inject the GSI script once and resolve when `window.google` is ready. */
function loadGoogleScript() {
  return new Promise((resolve, reject) => {
    if (typeof window === 'undefined') {
      reject(new Error('Google sign-in requires a browser environment'));
      return;
    }
    if (window.google?.accounts?.id) {
      resolve(window.google);
      return;
    }

    const existing = document.getElementById(GSI_SCRIPT_ID);
    if (existing) {
      existing.addEventListener('load', () => resolve(window.google), { once: true });
      existing.addEventListener('error', () => reject(new Error('Failed to load Google Identity Services')), { once: true });
      return;
    }

    const script = document.createElement('script');
    script.id = GSI_SCRIPT_ID;
    script.src = GSI_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve(window.google);
    script.onerror = () => reject(new Error('Failed to load Google Identity Services'));
    document.head.appendChild(script);
  });
}

/**
 * @param {object} handlers
 * @param {Function} handlers.onAuthenticated  Called with `{ token, user }`.
 * @param {Function} [handlers.onRoleRequired] Called with the Google profile for a new user.
 * @param {Function} [handlers.onError]
 */
export function useGoogleSignIn({ onAuthenticated, onRoleRequired, onError } = {}) {
  const clientId = import.meta.env?.VITE_GOOGLE_CLIENT_ID || DEFAULT_GOOGLE_CLIENT_ID;
  const buttonRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [pendingProfile, setPendingProfile] = useState(null);
  const [busy, setBusy] = useState(false);

  const handlersRef = useRef({ onAuthenticated, onRoleRequired, onError });
  useEffect(() => {
    handlersRef.current = { onAuthenticated, onRoleRequired, onError };
  }, [onAuthenticated, onRoleRequired, onError]);

  /** GSI hands us a credential; the backend decides what it means. */
  const handleCredential = useCallback(async (response) => {
    setBusy(true);
    try {
      const result = await authApi.googleVerify(response.credential);

      if (result?.status === 'requires_role') {
        setPendingProfile(result.googleData);
        handlersRef.current.onRoleRequired?.(result.googleData);
      } else if (result?.token) {
        handlersRef.current.onAuthenticated?.(result);
      } else {
        throw new Error(result?.error || 'استجابة غير متوقعة من الخادم');
      }
    } catch (err) {
      console.error('[GoogleSignIn] verification failed:', err.message);
      handlersRef.current.onError?.(err);
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => {
    if (!clientId) {
      // Not configured: the caller renders its own password form instead.
      return undefined;
    }

    let cancelled = false;

    loadGoogleScript()
      .then((google) => {
        if (cancelled) return;
        google.accounts.id.initialize({
          client_id: clientId,
          callback: handleCredential,
          cancel_on_tap_outside: false,
        });
        if (buttonRef.current) {
          google.accounts.id.renderButton(buttonRef.current, {
            theme: 'filled_black',
            size: 'large',
            type: 'standard',
            shape: 'rectangular',
          });
        }
        setReady(true);
      })
      .catch((err) => {
        if (cancelled) return;
        console.warn('[GoogleSignIn] unavailable:', err.message);
        handlersRef.current.onError?.(err);
      });

    return () => {
      cancelled = true;
    };
  }, [clientId, handleCredential]);

  /**
   * Second step for a brand-new Google user: create the account with a role.
   * A doctor comes back as `pending` and must wait for admin approval.
   */
  const completeRegistration = useCallback(
    async (role) => {
      if (!pendingProfile) return null;
      setBusy(true);
      try {
        const result = await authApi.googleComplete(pendingProfile, role);
        if (result?.status === 'pending') {
          setPendingProfile(null);
          return { pending: true, message: result.message };
        }
        if (result?.token) {
          setPendingProfile(null);
          handlersRef.current.onAuthenticated?.(result);
          return { pending: false };
        }
        throw new Error(result?.error || 'تعذر إنشاء الحساب');
      } catch (err) {
        handlersRef.current.onError?.(err);
        return null;
      } finally {
        setBusy(false);
      }
    },
    [pendingProfile],
  );

  return {
    buttonRef,
    ready,
    busy,
    configured: Boolean(clientId),
    pendingProfile,
    cancelRoleSelection: () => setPendingProfile(null),
    completeRegistration,
  };
}
